import { firstErrorCode, firstErrorContext, isGivmoError } from '@givmo/api-client';

/**
 * What a refused Books read lets a page say.
 *
 * The reader's 503 (`BOOKS_READ_UNAVAILABLE`) carries a coarse reason in `context.reason` to a
 * caller authorized for these books (BM-BE7): `not_set_up` (reading isn't switched on yet; no retry
 * helps), `temporary` (a later read may succeed) or `defect`. Without a reason — a defect, or a
 * refusal before authorization — the page says only what is true for every reason. The core
 * doorway's own 503 (authorization couldn't be answered) carries no error code, and is temporary.
 */
export type BooksRefusal =
  | 'absent'
  | 'not-provisioned'
  | 'not-set-up'
  | 'temporary'
  | 'unavailable'
  | 'error';

export function booksRefusal(error: unknown): BooksRefusal {
  if (!isGivmoError(error)) return 'error';
  if (error.status === 404) return 'absent';
  const code = firstErrorCode(error);
  if (error.status === 409 && code === 'BOOKS_NOT_PROVISIONED') return 'not-provisioned';
  if (error.status === 503 && code === null) return 'temporary';
  if (error.status === 503 && code === 'BOOKS_READ_UNAVAILABLE') {
    const reason = firstErrorContext(error)?.reason;
    if (reason === 'not_set_up') return 'not-set-up';
    if (reason === 'temporary') return 'temporary';
    return 'unavailable';
  }
  return 'error';
}

export const BOOKS_REFUSAL_COPY: Record<
  BooksRefusal,
  { title: string; description?: string; retry: boolean }
> = {
  absent: {
    title: 'Books aren’t available',
    description: 'Books aren’t available for this organization.',
    retry: false,
  },
  'not-provisioned': {
    title: 'Books setup isn’t complete',
    description: 'You can open Books once setup finishes.',
    retry: false,
  },
  'not-set-up': {
    title: 'Books can’t be read yet',
    description:
      'Reading these books hasn’t been switched on yet. There’s nothing you need to do, and Books will open here once it is.',
    retry: false,
  },
  temporary: {
    title: 'Books can’t be opened right now',
    description: 'Your books couldn’t be read just now. Try again in a few minutes.',
    retry: true,
  },
  unavailable: { title: 'Books can’t be read right now', retry: true },
  error: { title: 'Couldn’t load Books', retry: true },
};

/** The refusal's first entry: its code and context, for a statement's own refusals. */
export function refusalOf(error: unknown): {
  code: string | null;
  context: Record<string, unknown> | null;
  detail: string | null;
} {
  if (!isGivmoError(error)) return { code: null, context: null, detail: null };
  return {
    code: firstErrorCode(error),
    context: firstErrorContext(error),
    detail: error.entries[0]?.detail ?? null,
  };
}
